const toNumber = (value, fallback = 0) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
};

const roundMoney = (value) => Math.round(toNumber(value, 0) * 100) / 100;

const HIDDEN_MARGIN_LABEL_REGEX = /\bhomevilas\s+margin\b/i;
const SUPPLIER_BASE_LABEL_REGEX = /\bsupplier\s+base\b/i;
const BREAKDOWN_BASE_LABEL_REGEX = /\b(supplier\s+base|stay\s+base|stay\s+total|accommodation|nightly)\b/i;
const INVOICE_BASE_LABEL_REGEX = /\b(accommodation|nightly|stay\s+base|stay\s+total|supplier\s+base)\b/i;
const hasNormalizedCustomerPricing = (source = {}) =>
  source?.base_price !== undefined ||
  source?.taxable_subtotal !== undefined ||
  source?.final_payable !== undefined;

const readLabel = (entry = {}) =>
  String(
    entry.label ||
      entry.display_name ||
      entry.displayName ||
      entry.description ||
      entry.name ||
      entry.rule_name ||
      entry.ruleName ||
      ''
  ).trim();

const readType = (entry = {}) =>
  String(entry.type || entry.charge_type || entry.chargeType || '').trim().toLowerCase();

export const isHiddenCustomerCharge = (entry = {}) => {
  const type = readType(entry);
  if (type === 'margin' || type === 'margin_percent') return true;
  return HIDDEN_MARGIN_LABEL_REGEX.test(readLabel(entry));
};

const sumHiddenCharges = (items = []) =>
  roundMoney(
    (Array.isArray(items) ? items : []).reduce((sum, entry) => {
      if (!isHiddenCustomerCharge(entry)) return sum;
      return sum + toNumber(entry.amount ?? entry.value ?? entry.total ?? entry.unitPrice, 0);
    }, 0)
  );

export const getCustomerHiddenChargeAmount = (source = {}) => {
  const directMargin = roundMoney(source?.homevilas_margin_amount);
  if (directMargin) return directMargin;

  const listSources = [source?.line_item_breakdown, source?.breakdownItems, source?.lineItems];
  for (const items of listSources) {
    const hidden = sumHiddenCharges(items);
    if (hidden) return hidden;
  }

  const taxSources = [
    source?.tax_fee_breakdown || source?.taxFeeBreakdown,
    source?.taxes_and_fees || source?.taxesAndFees,
  ];
  for (const breakdown of taxSources) {
    if (!breakdown || typeof breakdown !== 'object') continue;
    const hidden =
      sumHiddenCharges(breakdown.fee_lines || breakdown.fees || []) +
      sumHiddenCharges(breakdown.tax_lines || breakdown.taxes || []);
    if (hidden) return roundMoney(hidden);
  }

  return 0;
};

export const getCustomerVisibleBaseAmount = (source = {}, baseAmount = 0) =>
  roundMoney(toNumber(baseAmount, 0) + getCustomerHiddenChargeAmount(source));

export const sanitizeCustomerBreakdownItems = (items = []) => {
  const hiddenAmount = sumHiddenCharges(items);
  const visible = (Array.isArray(items) ? items : []).filter((item) => !isHiddenCustomerCharge(item));
  if (!visible.length || !hiddenAmount) return visible;

  const baseIndex = visible.findIndex((item) => readType(item) === 'base' || BREAKDOWN_BASE_LABEL_REGEX.test(readLabel(item)));
  if (baseIndex >= 0) {
    const target = visible[baseIndex];
    visible[baseIndex] = {
      ...target,
      label: SUPPLIER_BASE_LABEL_REGEX.test(readLabel(target)) ? 'Base Price' : target.label || 'Base Price',
      amount: roundMoney(toNumber(target.amount, 0) + hiddenAmount),
    };
    return visible;
  }

  return [{ label: 'Base Price', amount: roundMoney(hiddenAmount), type: 'base' }, ...visible];
};

export const sanitizeCustomerInvoiceLineItems = (lineItems = []) => {
  const hiddenAmount = sumHiddenCharges(lineItems);
  const visible = (Array.isArray(lineItems) ? lineItems : []).filter((item) => !isHiddenCustomerCharge(item));
  if (!visible.length || !hiddenAmount) return visible;

  const baseIndex = visible.findIndex((item) => INVOICE_BASE_LABEL_REGEX.test(readLabel(item)));
  if (baseIndex >= 0) {
    const target = visible[baseIndex];
    const quantity = Math.max(1, toNumber(target.quantity ?? target.qty, 1));
    const amount = roundMoney(toNumber(target.amount, 0) + hiddenAmount);
    visible[baseIndex] = {
      ...target,
      description: SUPPLIER_BASE_LABEL_REGEX.test(readLabel(target))
        ? 'Accommodation'
        : target.description || target.label || 'Accommodation',
      quantity,
      unitPrice: roundMoney(amount / quantity),
      amount,
    };
    return visible;
  }

  return [{ description: 'Accommodation', quantity: 1, unitPrice: hiddenAmount, amount: hiddenAmount }, ...visible];
};

export const sanitizeCustomerQuote = (quote) => {
  if (!quote || typeof quote !== 'object') return quote;
  const hiddenAmount = getCustomerHiddenChargeAmount(quote);
  const add = (value) => roundMoney(toNumber(value, 0) + hiddenAmount);
  const next = { ...quote };

  if (hiddenAmount && !hasNormalizedCustomerPricing(next)) {
    if (next.base_nights_amount !== undefined) next.base_nights_amount = add(next.base_nights_amount);
    if (next.roomSubtotal !== undefined) next.roomSubtotal = add(next.roomSubtotal);
    if (next.baseRoomSubtotal !== undefined) next.baseRoomSubtotal = add(next.baseRoomSubtotal);
    if (next.base !== undefined) next.base = add(next.base);
    if (next.pricing && typeof next.pricing === 'object' && next.pricing.base !== undefined) {
      next.pricing = { ...next.pricing, base: add(next.pricing.base) };
    }
    if (next.totals && typeof next.totals === 'object') {
      next.totals = {
        ...next.totals,
        ...(next.totals.roomSubtotal !== undefined ? { roomSubtotal: add(next.totals.roomSubtotal) } : {}),
        ...(next.totals.base !== undefined ? { base: add(next.totals.base) } : {}),
        ...(next.totals.baseRoomSubtotal !== undefined ? { baseRoomSubtotal: add(next.totals.baseRoomSubtotal) } : {}),
      };
    }
  }

  if (Array.isArray(next.line_item_breakdown)) next.line_item_breakdown = sanitizeCustomerBreakdownItems(next.line_item_breakdown);
  if (Array.isArray(next.breakdownItems)) next.breakdownItems = sanitizeCustomerBreakdownItems(next.breakdownItems);

  delete next.homevilas_margin_amount;
  delete next.homevilas_revenue_before_discount;
  delete next.homevilas_revenue_after_discount;
  delete next.applied_margin_rule;
  return next;
};
